import type { CountryData } from 'react-intl-tel-input'

import { GENERIC_MASK, allCountries } from './consts'
import { masks } from './masks'
import type { CountryCodes } from './types'

const nonDigitsPattern = /\D/g

function resetPhoneNumberFormatting(phoneNumber: string): string {
  return phoneNumber.replace(nonDigitsPattern, '')
}

export function formatCountryCode(countryCode: string): CountryCodes {
  return countryCode.toUpperCase() as CountryCodes
}

export function findCountryId(
  formattedPhoneNumber: string,
): CountryCodes | undefined {
  const phoneNumber = resetPhoneNumberFormatting(formattedPhoneNumber)

  // find the best match by dial code
  let maxMatchLength = 0
  const countries = allCountries
    .reduce<Array<{ country: CountryData; matchLength: number }>>(
      (order, country) => {
        if (!country.dialCode || !country.iso2) {
          return order
        }

        const match = phoneNumber.match(getDialCodePattern(country.dialCode))

        if (match) {
          const matchLength = match[0].length

          order.push({ country, matchLength })

          maxMatchLength = Math.max(maxMatchLength, matchLength)
        }

        return order
      },
      [],
    )
    .filter((country) => country.matchLength === maxMatchLength)
    .map((c) => c.country)

  if (countries.length === 1 && countries[0].iso2) {
    return formatCountryCode(countries[0].iso2)
  }

  const matchedAreaCodeCountry = countries.find((country) => {
    if (!country.dialCode) return false

    const nationalNumber = phoneNumber.replace(
      getDialCodePattern(country?.dialCode),
      '',
    )

    return country.areaCodes?.some((areaCode) =>
      nationalNumber.startsWith(areaCode),
    )
  })

  if (countries.length > 0 && !matchedAreaCodeCountry) {
    const morePriorityCountry = [...countries].sort(
      (a, b) => (b.priority ?? 0) - (a.priority ?? 0),
    )[0]

    if (morePriorityCountry.iso2) {
      return formatCountryCode(morePriorityCountry.iso2)
    }
  }

  if (matchedAreaCodeCountry?.iso2) {
    return formatCountryCode(matchedAreaCodeCountry.iso2)
  }
}

export function findDialCode(countryId: CountryCodes): string {
  const country = allCountries.filter(
    (country) => country.iso2 === countryId.toLowerCase(),
  )

  const dialCode = country[0]?.dialCode
  const areaCodes = country[0]?.areaCodes

  if (!dialCode) {
    return ''
  }

  const countriesWithSameDialCode = allCountries.filter(
    (country) => country.dialCode === dialCode,
  )

  if (countriesWithSameDialCode.length === 1 || !areaCodes) {
    return dialCode
  }

  return dialCode + areaCodes[0]
}

export function getDialCodePattern(dialCode: string) {
  return new RegExp(`^\\+?${dialCode}`)
}

export function getPhoneNumberMask(phoneNumber: string): string {
  const cleaned = resetPhoneNumberFormatting(phoneNumber)
  const countryCode = findCountryId(cleaned)
  const countryMasks = countryCode ? masks[countryCode] : null

  if (!countryCode || !countryMasks) {
    return GENERIC_MASK
  }

  return (
    countryMasks.find((mask) => {
      const maskDigits = mask.replace(/[^#]/g, '').length

      return cleaned.length <= maskDigits
    }) || countryMasks[0]
  )
}

export const applyPhoneNumberMask = (
  phoneNumber: string,
  mask: string,
): string => {
  const cleaned = resetPhoneNumberFormatting(phoneNumber)

  let result = ''
  let maskIndex = 0
  let numberIndex = 0

  while (maskIndex < mask.length && numberIndex < cleaned.length) {
    if (mask[maskIndex] === '#') {
      result += cleaned[numberIndex]
      numberIndex++
    } else {
      result += mask[maskIndex]
    }
    maskIndex++
  }

  if (numberIndex < cleaned.length) {
    result += cleaned.slice(numberIndex)
  }

  return result
}

export function isSupportedCountry(
  countryCode: string,
): countryCode is CountryCodes {
  return masks[countryCode as CountryCodes] !== undefined
}
